import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  useTheme,
  ActivityIndicator,
  Button,
  Text,
  Card,
  Switch,
  Chip,
} from 'react-native-paper';

import { StudyGroupRecommendations } from './StudyGroupRecommendations';
import { CollaborativeLearningActivities } from './CollaborativeLearningActivities';
import { GroupProgressTracker } from './GroupProgressTracker';
import { GroupChallengeCreator } from './GroupChallengeCreator';
import { StudyGroupAnalytics } from './StudyGroupAnalytics';
import type { AppDispatch, RootState } from '../../store';

import { logger } from '../../utils/logger';
import {
  fetchStudyGroupRecommendations,
  generateOptimalGroupFormation,
  selectStudyGroupRecommendations,
  selectMyStudyGroups,
  selectStudyGroupLoading,
  selectStudyGroupError,
  type StudyGroupRecommendation,
  type CollaborativeLearningActivity,
  type GroupChallenge,
  type ProgressRecommendation,
} from '../../store/slices/studyGroupSlice';

interface IntelligentStudyGroupHubProps {
  userId: string;
  onNavigateToGroup?: (groupId: string) => void;
  onNavigateToActivity?: (activityId: string) => void;
  onNavigateToChallenge?: (challengeId: string) => void;
}

export const IntelligentStudyGroupHub: React.FC<
  IntelligentStudyGroupHubProps
> = ({
  userId,
  onNavigateToGroup,
  onNavigateToActivity,
  onNavigateToChallenge,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const recommendations = useSelector(selectStudyGroupRecommendations);
  const myGroups = useSelector(selectMyStudyGroups);
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<
    'recommendations' | 'activities' | 'progress' | 'challenges' | 'analytics'
  >('recommendations');
  const [refreshing, setRefreshing] = useState(false);
  const [aiOptimizationEnabled, setAiOptimizationEnabled] = useState(true);

  // Mock data for demonstration
  const mockMemberSkills = {
    [userId]: ['espresso', 'milk_steaming', 'brewing_fundamentals'],
    user2: ['pour_over', 'grind_consistency', 'water_quality'],
    user3: ['brewing_fundamentals', 'equipment_maintenance'],
  };

  const mockMemberSkillLevels = {
    [userId]: 0.7,
    user2: 0.5,
    user3: 0.3,
  };

  const mockLearningGoals = [
    'espresso_mastery',
    'latte_art',
    'brewing_consistency',
    'advanced_techniques',
  ];

  useEffect(() => {
    loadInitialData();
  }, [userId]);

  useEffect(() => {
    if (myGroups.length > 0 && !selectedGroupId) {
      setSelectedGroupId(myGroups[0].id);
    }
  }, [myGroups, selectedGroupId]);

  const loadInitialData = async () => {
    try {
      await dispatch(fetchStudyGroupRecommendations(userId)).unwrap();
    } catch (err) {
      logger.error('Failed to load initial study group data:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleOptimizeGroupFormation = async () => {
    if (!aiOptimizationEnabled) return;

    try {
      const optimizationParams = {
        members: [userId],
        learningGoals: mockLearningGoals,
        skillLevels: { [userId]: mockMemberSkillLevels[userId] || 0.5 },
        preferences: {
          groupSize: 'small_group',
          timeZone: 'UTC-8',
          availability: ['evening', 'weekend'],
          learningStyle: 'collaborative',
          focusAreas: mockLearningGoals,
        },
      };

      await dispatch(
        generateOptimalGroupFormation(optimizationParams)
      ).unwrap();

      Alert.alert(
        'AI Optimization Complete',
        'Study group recommendations have been optimized based on your learning profile and preferences.',
        [
          {
            text: 'View Results',
            onPress: () => setActiveView('recommendations'),
          },
        ]
      );
    } catch (err) {
      Alert.alert(
        'Optimization Failed',
        'Unable to optimize group formation. Please try again later.'
      );
    }
  };

  const handleGroupSelect = (recommendation: StudyGroupRecommendation) => {
    setSelectedGroupId(recommendation.group.id);
    onNavigateToGroup?.(recommendation.group.id);
  };

  const handleJoinGroup = async (groupId: string) => {
    try {
      Alert.alert('Success', 'You have joined the study group!');
      await loadInitialData();
    } catch (err) {
      Alert.alert('Error', 'Failed to join study group. Please try again.');
    }
  };

  const handleActivitySelect = (activity: CollaborativeLearningActivity) => {
    onNavigateToActivity?.(activity.id);
  };

  const handleActivityStart = (activityId: string) => {
    Alert.alert(
      'Start Activity',
      'This would start the collaborative learning activity.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Start', onPress: () => onNavigateToActivity?.(activityId) },
      ]
    );
  };

  const handleChallengeCreated = (challenge: GroupChallenge) => {
    Alert.alert(
      'Challenge Created',
      `"${challenge.title}" has been created and balanced for your group's skill levels.`,
      [
        {
          text: 'View Challenge',
          onPress: () => onNavigateToChallenge?.(challenge.id),
        },
      ]
    );
  };

  const handleChallengeStart = (challengeId: string) => {
    onNavigateToChallenge?.(challengeId);
  };

  const handleProgressRecommendationSelect = (
    recommendation: ProgressRecommendation
  ) => {
    Alert.alert('AI Recommendation', recommendation.description, [
      { text: 'Dismiss', style: 'cancel' },
      {
        text: 'Implement',
        onPress: () =>
          logger.log('Implementing recommendation:', recommendation.title),
      },
    ]);
  };

  const handleAnalyticsExport = (format: 'pdf' | 'csv') => {
    Alert.alert(
      'Export Analytics',
      `Analytics data would be exported as ${format.toUpperCase()} format.`,
      [{ text: 'OK' }]
    );
  };

  const renderViewSelector = () => {
    const views = [
      { key: 'recommendations', label: 'AI Groups', icon: 'people' },
      { key: 'activities', label: 'Activities', icon: 'school' },
      { key: 'progress', label: 'Progress', icon: 'trending-up' },
      { key: 'challenges', label: 'Challenges', icon: 'trophy' },
      { key: 'analytics', label: 'Analytics', icon: 'analytics' },
    ];

    return (
      <View style={styles.viewSelector}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.viewSelectorContent}
        >
          {views.map(view => (
            <TouchableOpacity
              key={view.key}
              style={[
                styles.viewButton,
                activeView === view.key && styles.activeViewButton,
              ]}
              onPress={() => setActiveView(view.key as any)}
            >
              <Ionicons
                name={view.icon as any}
                size={16}
                color={
                  activeView === view.key
                    ? theme.colors.primary
                    : theme.colors.onSurfaceVariant
                }
              />
              <Text
                style={[
                  styles.viewButtonText,
                  {
                    color:
                      activeView === view.key
                        ? theme.colors.primary
                        : theme.colors.onSurfaceVariant,
                  },
                ]}
              >
                {view.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderAIOptimizationPanel = () => {
    return (
      <Card style={styles.aiPanel}>
        <Card.Content>
          <View style={styles.aiPanelHeader}>
            <Ionicons name='bulb' size={20} color={theme.colors.primary} />
            <Text variant='titleMedium' style={styles.aiPanelTitle}>
              AI-Powered Study Groups
            </Text>
            <Switch
              value={aiOptimizationEnabled}
              onValueChange={setAiOptimizationEnabled}
            />
          </View>
          <Text variant='bodyMedium' style={styles.aiPanelDescription}>
            AI analyzes your learning patterns, skill levels, and goals to
            recommend optimal study groups and activities.
          </Text>
          {aiOptimizationEnabled && (
            <Button
              mode='outlined'
              onPress={handleOptimizeGroupFormation}
              style={styles.optimizeButton}
              icon='settings'
            >
              Optimize My Groups
            </Button>
          )}
        </Card.Content>
      </Card>
    );
  };

  const renderGroupSelector = () => {
    if (myGroups.length === 0) return null;

    return (
      <View style={styles.groupSelector}>
        <Text variant='labelMedium' style={styles.groupSelectorLabel}>
          Active Group:
        </Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.groupSelectorContent}
        >
          {myGroups.map(group => (
            <Chip
              key={group.id}
              selected={selectedGroupId === group.id}
              onPress={() => setSelectedGroupId(group.id)}
              style={styles.groupChip}
            >
              {group.name}
            </Chip>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderEmptyState = (icon: string, title: string, subtitle: string) => (
    <View style={styles.emptyState}>
      <Ionicons
        name={icon as any}
        size={64}
        color={theme.colors.onSurfaceVariant}
      />
      <Text variant='headlineSmall' style={styles.emptyStateText}>
        {title}
      </Text>
      <Text variant='bodyMedium' style={styles.emptyStateSubtext}>
        {subtitle}
      </Text>
    </View>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'recommendations':
        return (
          <StudyGroupRecommendations
            userId={userId}
            onGroupSelect={handleGroupSelect}
            onJoinGroup={handleJoinGroup}
            showOptimization={aiOptimizationEnabled}
            maxRecommendations={10}
          />
        );

      case 'activities':
        if (!selectedGroupId) {
          return renderEmptyState(
            'school-outline',
            'Join a study group first',
            'Collaborative learning activities are available once you join a group'
          );
        }
        return (
          <CollaborativeLearningActivities
            groupId={selectedGroupId}
            memberSkills={mockMemberSkills}
            learningGoals={mockLearningGoals}
            timeConstraints={60}
            onActivitySelect={handleActivitySelect}
            onActivityStart={handleActivityStart}
            showGenerateButton={true}
          />
        );

      case 'progress':
        if (!selectedGroupId) {
          return renderEmptyState(
            'trending-up-outline',
            'No group selected',
            'Select a study group to view collective progress insights'
          );
        }
        return (
          <GroupProgressTracker
            groupId={selectedGroupId}
            showDetailedAnalytics={true}
            onRecommendationSelect={handleProgressRecommendationSelect}
          />
        );

      case 'challenges':
        if (!selectedGroupId) {
          return renderEmptyState(
            'trophy-outline',
            'No group selected',
            'Join a study group to create and participate in AI-balanced challenges'
          );
        }
        return (
          <GroupChallengeCreator
            groupId={selectedGroupId}
            memberSkills={mockMemberSkillLevels}
            onChallengeCreated={handleChallengeCreated}
            onChallengeStart={handleChallengeStart}
          />
        );

      case 'analytics':
        if (!selectedGroupId) {
          return renderEmptyState(
            'analytics-outline',
            'No group selected',
            'Select a study group to view detailed analytics and engagement metrics'
          );
        }
        return (
          <StudyGroupAnalytics
            groupId={selectedGroupId}
            showExportOptions={true}
            onExport={handleAnalyticsExport}
          />
        );

      default:
        return null;
    }
  };

  if (loading.recommendations && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size='large' />
        <Text variant='bodyLarge' style={styles.loadingText}>
          Loading intelligent study group features...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* AI Optimization Panel */}
      {renderAIOptimizationPanel()}

      {/* View Selector */}
      {renderViewSelector()}

      {/* Group Selector */}
      {renderGroupSelector()}

      {/* Error Display */}
      {error && (
        <Card style={styles.errorContainer}>
          <Card.Content>
            <Text
              variant='bodyLarge'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              {error}
            </Text>
            <Button mode='outlined' onPress={loadInitialData}>
              Retry
            </Button>
          </Card.Content>
        </Card>
      )}

      {/* Content */}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    marginTop: 16,
    textAlign: 'center',
  },
  aiPanel: {
    margin: 16,
  },
  aiPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  aiPanelTitle: {
    marginLeft: 8,
    flex: 1,
  },
  aiPanelDescription: {
    marginBottom: 12,
  },
  optimizeButton: {
    alignSelf: 'flex-start',
  },
  viewSelector: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  viewSelectorContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
  },
  activeViewButton: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  viewButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  groupSelector: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  groupSelectorLabel: {
    marginBottom: 8,
  },
  groupSelectorContent: {
    flexDirection: 'row',
  },
  groupChip: {
    marginRight: 8,
  },
  content: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
    paddingHorizontal: 32,
  },
  emptyStateText: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    textAlign: 'center',
    lineHeight: 20,
  },
  errorContainer: {
    margin: 16,
  },
  errorText: {
    textAlign: 'center',
    marginBottom: 16,
  },
});
